import React from 'react';
import { useTranslation } from 'react-i18next';
import { Rocket } from 'lucide-react';

export const BoostHistorySection = ({ boostHistory, setBoostModal, setSelectedTier }) => {
  const { t } = useTranslation();
  if (!boostHistory.length) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="boost-history-section">
      <div className="p-4 border-b">
        <h2 className="font-semibold text-gray-900 flex items-center gap-2">
          <Rocket className="h-5 w-5 text-amber-500" />
          {t('boostHistory')}
        </h2>
      </div>
      <div className="p-4 space-y-3">
        {boostHistory.map((b, idx) => {
          const increase = b.viewsBefore > 0
            ? Math.round((b.viewsDuring - b.viewsBefore) / b.viewsBefore * 100)
            : b.viewsDuring > 0 ? 100 : 0;
          const isTop = b.isTopPerforming;
          return (
            <div key={b.id} className={`rounded-xl border p-3 ${isTop ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-gray-50'}`}
              data-testid={`boost-history-card-${idx}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 text-sm truncate">{b.postTitle}</h4>
                    {isTop && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-400 text-white text-[10px] font-bold rounded-full flex-shrink-0" data-testid="top-boost-badge">
                        {t('topBoost')}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-0.5">{b.tierLabel} &middot; {new Date(b.activatedAt).toLocaleDateString()}</p>
                </div>
                <span className={`text-xs font-bold px-2 py-1 rounded-lg ${b.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                  {b.status === 'active' ? t('boostStatusActive') : t('boostStatusEnded')}
                </span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-lg font-bold text-gray-700">{b.viewsBefore}</p>
                  <p className="text-[10px] text-gray-400">{t('viewsBefore')}</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-purple-200">
                  <p className="text-lg font-bold text-purple-600">{b.viewsDuring}</p>
                  <p className="text-[10px] text-gray-400">{t('viewsDuring')}</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-lg font-bold text-gray-700">{b.totalClicks}</p>
                  <p className="text-[10px] text-gray-400">{t('analyticsClicks')}</p>
                </div>
              </div>
              {increase > 0 && (
                <div className="mt-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg text-center" data-testid={`boost-increase-${idx}`}>
                  <span className="text-sm font-bold text-green-700">+{increase}% {t('moreViewsDuringBoost')}</span>
                </div>
              )}
              {b.status === 'ended' && (
                <button onClick={() => { setBoostModal({ postId: b.postId, postType: b.postType, title: b.postTitle }); setSelectedTier(b.tierId); }}
                  className="mt-2 w-full py-2 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-1.5 hover:from-amber-500 hover:to-orange-600 transition-all"
                  data-testid={`boost-again-btn-${idx}`}>
                  <Rocket className="h-3.5 w-3.5" />
                  {increase >= 50 ? t('boostAgainStrong') : t('boostAgain')}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
